import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FOCUS_SESSION_KEY = 'rush.focusSession';
const FOCUS_LAST_SCOPE_KEY = 'rush.focusSessionLast';

const URGENCY_RANK = { Overdue: 0, 'Due today': 1, 'This week': 2, Later: 3 };

const TYPE_LABEL = {
    translate: 'Translate',
    review: 'Review',
    approve: 'Approve',
    comment: 'Comment',
    glossary: 'Glossary',
};

const estimateMinutes = (t) => {
    if (t.words) {
        const wpm = t.type === 'review' ? 8 : t.type === 'approve' ? 18 : 3;
        return Math.max(2, Math.round(t.words / wpm));
    }
    const perSeg = t.type === 'review' ? 2 : t.type === 'approve' ? 1 : 3.5;
    return Math.max(2, Math.round((t.segs || 1) * perSeg));
};

const formatMinutes = (min) => {
    if (!min || min < 1) return '—';
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
};

const sortByUrgency = (arr) => arr.slice().sort((a, b) => {
    const ua = URGENCY_RANK[a.grp] ?? 9;
    const ub = URGENCY_RANK[b.grp] ?? 9;
    if (ua !== ub) return ua - ub;
    return estimateMinutes(a) - estimateMinutes(b);
});

const resolveScope = (tasks, scope) => {
    let pool = tasks.slice();
    if (scope.mode === 'urgency') {
        if (scope.value === 'overdue') {
            pool = pool.filter((t) => t.grp === 'Overdue');
        } else {
            pool = pool.filter((t) => t.grp === 'Overdue' || t.grp === 'Due today');
        }
    } else if (scope.mode === 'type') {
        pool = pool.filter((t) => t.type === scope.value);
    }
    pool = sortByUrgency(pool);
    if (scope.mode === 'count') pool = pool.slice(0, scope.value || 5);
    return pool;
};

const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};

const readSession = () => safeParse(localStorage.getItem(FOCUS_SESSION_KEY));
const notifyFocusChange = () => {
    try { window.dispatchEvent(new Event('rush:focus-changed')); } catch {}
};
const writeSession = (s) => {
    try { localStorage.setItem(FOCUS_SESSION_KEY, JSON.stringify(s)); } catch {}
    notifyFocusChange();
};
const clearStoredSession = () => {
    try { localStorage.removeItem(FOCUS_SESSION_KEY); } catch {}
    notifyFocusChange();
};
const readLastScope = () => safeParse(localStorage.getItem(FOCUS_LAST_SCOPE_KEY));
const writeLastScope = (s) => { try { localStorage.setItem(FOCUS_LAST_SCOPE_KEY, JSON.stringify(s)); } catch {} };

export const hasActiveFocusSession = () => {
    const s = readSession();
    return !!(s && Array.isArray(s.orderedIds) && s.orderedIds.length > 0);
};

// ---- One-time style injection ----
const FOCUS_CSS = `
.rq-focus-shell { position: fixed; inset: 0; z-index: 9000; background: var(--bg, #fbfaf7); display: flex; flex-direction: column; }
body.rs-dark .rq-focus-shell { background: var(--bg, #0c0c0e); }
.rq-focus-top { display: flex; align-items: center; justify-content: space-between; padding: 18px 28px; border-bottom: 1px solid var(--line); }
.rq-focus-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--ink-4); }
.rq-focus-timerwrap { display: flex; align-items: center; gap: 12px; }
.rq-focus-timer-txt { font-size: 12.5px; color: var(--ink-3); }
.rq-focus-exit { background: transparent; border: 1px solid var(--line); border-radius: 8px; padding: 6px 12px; font: inherit; font-size: 12.5px; cursor: pointer; color: var(--ink-3); }
.rq-focus-exit:hover { color: var(--ink); }
.rq-focus-body { flex: 1; display: flex; align-items: center; justify-content: center; padding: 28px; }
.rq-focus-card { width: 100%; max-width: 620px; background: var(--bg-1, #fff); border: 1px solid var(--line); border-radius: 18px; padding: 32px 32px 28px; box-shadow: 0 18px 48px rgba(0,0,0,0.08); }
body.rs-dark .rq-focus-card { background: var(--bg-1, #15151a); }
.rq-focus-typechip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; background: var(--accent-soft, #ecedff); color: var(--accent-2, #3838c0); margin-bottom: 14px; }
.rq-focus-title { font-size: 22px; font-weight: 700; line-height: 1.3; color: var(--ink); }
.rq-focus-meta { font-size: 13px; color: var(--ink-3); margin-top: 10px; }
.rq-focus-size { font-size: 13px; color: var(--ink-3); margin-top: 6px; }
.rq-focus-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px; flex-wrap: wrap; }
.rq-focus-btn { background: var(--bg-soft, #f3f1ec); border: 1px solid var(--line); border-radius: 10px; padding: 9px 16px; font: inherit; font-size: 13.5px; font-weight: 500; cursor: pointer; color: var(--ink); }
.rq-focus-btn:hover { background: var(--bg, #ece9e3); }
.rq-focus-btn:disabled { opacity: .5; cursor: not-allowed; }
.rq-focus-btn.primary { background: var(--accent, #4a4ae8); color: #fff; border-color: var(--accent, #4a4ae8); }
.rq-focus-btn.primary:hover { background: var(--accent-2, #3838c0); border-color: var(--accent-2, #3838c0); }
.rq-focus-setup-grid { display: grid; gap: 18px; margin-top: 20px; }
.rq-focus-setup-row { display: flex; flex-direction: column; gap: 8px; }
.rq-focus-setup-row > label { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); }
.rq-focus-opts { display: flex; flex-wrap: wrap; gap: 6px; }
.rq-focus-opt { padding: 8px 14px; border: 1px solid var(--line); border-radius: 999px; background: transparent; font: inherit; font-size: 13px; cursor: pointer; color: var(--ink); }
.rq-focus-opt:hover { background: var(--bg-soft, #f3f1ec); }
.rq-focus-opt.on { background: var(--accent, #4a4ae8); color: #fff; border-color: var(--accent, #4a4ae8); }
.rq-focus-modal-back { position: fixed; inset: 0; background: rgba(8,8,12,0.45); display: flex; align-items: center; justify-content: center; z-index: 9100; }
.rq-focus-modal { background: var(--bg-1, #fff); border-radius: 14px; padding: 24px 26px; max-width: 420px; width: calc(100% - 32px); box-shadow: 0 24px 64px rgba(0,0,0,0.18); }
body.rs-dark .rq-focus-modal { background: var(--bg-1, #15151a); }
.rq-focus-modal h3 { margin: 0 0 8px; font-size: 17px; font-weight: 700; }
.rq-focus-modal p { margin: 0 0 18px; font-size: 13.5px; color: var(--ink-3); line-height: 1.5; }
.rq-focus-modal-actions { display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap; }
.rq-focus-end-num { font-size: 38px; font-weight: 800; color: var(--ink); line-height: 1.15; }
.rq-focus-end-sub { font-size: 14px; color: var(--ink-3); margin-top: 6px; }
.rq-focus-breakdown { margin-top: 22px; display: flex; flex-direction: column; gap: 6px; text-align: left; }
.rq-focus-breakdown-row { display: flex; justify-content: space-between; font-size: 13px; padding: 6px 0; border-bottom: 1px solid var(--line); }
.rq-focus-breakdown-row:last-child { border-bottom: 0; }
`;
const ensureFocusCss = () => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('rq-focus-css')) return;
    const s = document.createElement('style');
    s.id = 'rq-focus-css';
    s.textContent = FOCUS_CSS;
    document.head.appendChild(s);
};

const FocusRing = ({ pct }) => {
    const r = 14;
    const c = 2 * Math.PI * r;
    const off = c * (1 - Math.max(0, Math.min(1, pct)));
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" style={{ display: 'block' }}>
            <circle cx="18" cy="18" r={r} fill="none" stroke="var(--line)" strokeWidth="3" />
            <circle cx="18" cy="18" r={r} fill="none" stroke="var(--accent, #4a4ae8)" strokeWidth="3"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                transform="rotate(-90 18 18)" />
        </svg>
    );
};

const FocusSetup = ({ tasks, onCancel, onStart }) => {
    const last = readLastScope();
    const [mode, setMode] = useState(last?.mode || 'urgency');
    const [urgencyVal, setUrgencyVal] = useState(last?.mode === 'urgency' ? last.value : 'today');
    const [countVal, setCountVal] = useState(last?.mode === 'count' ? last.value : 5);
    const [typeVal, setTypeVal] = useState(last?.mode === 'type' ? last.value : 'translate');
    const [durationMin, setDurationMin] = useState(last?.durationMin ?? null);

    const scope = mode === 'urgency'
        ? { mode, value: urgencyVal, durationMin }
        : mode === 'count'
            ? { mode, value: countVal, durationMin }
            : { mode, value: typeVal, durationMin };

    const preview = useMemo(() => resolveScope(tasks, scope), [tasks, scope.mode, scope.value, scope.durationMin]);
    const previewMin = preview.reduce((a, t) => a + estimateMinutes(t), 0);

    return (
        <div className="rq-focus-shell">
            <div className="rq-focus-top">
                <div className="rq-focus-eyebrow">Focus session · Setup</div>
                <button type="button" className="rq-focus-exit" onClick={onCancel}>Cancel</button>
            </div>
            <div className="rq-focus-body">
                <div className="rq-focus-card">
                    <div className="rq-focus-title">What are you focusing on?</div>
                    <div className="rq-focus-meta">
                        Pick a scope. We'll walk you through one task at a time, in urgency order, and hide everything else.
                    </div>

                    <div className="rq-focus-setup-grid">
                        <div className="rq-focus-setup-row">
                            <label>Scope</label>
                            <div className="rq-focus-opts">
                                <button type="button" className={`rq-focus-opt ${mode === 'urgency' ? 'on' : ''}`} onClick={() => setMode('urgency')}>By urgency</button>
                                <button type="button" className={`rq-focus-opt ${mode === 'count' ? 'on' : ''}`} onClick={() => setMode('count')}>By count</button>
                                <button type="button" className={`rq-focus-opt ${mode === 'type' ? 'on' : ''}`} onClick={() => setMode('type')}>By type</button>
                            </div>
                        </div>

                        {mode === 'urgency' && (
                            <div className="rq-focus-setup-row">
                                <label>Which</label>
                                <div className="rq-focus-opts">
                                    <button type="button" className={`rq-focus-opt ${urgencyVal === 'today' ? 'on' : ''}`} onClick={() => setUrgencyVal('today')}>Everything due today</button>
                                    <button type="button" className={`rq-focus-opt ${urgencyVal === 'overdue' ? 'on' : ''}`} onClick={() => setUrgencyVal('overdue')}>Overdue only</button>
                                </div>
                            </div>
                        )}

                        {mode === 'count' && (
                            <div className="rq-focus-setup-row">
                                <label>How many</label>
                                <div className="rq-focus-opts">
                                    {[3, 5, 10].map((n) => (
                                        <button type="button" key={n} className={`rq-focus-opt ${countVal === n ? 'on' : ''}`} onClick={() => setCountVal(n)}>Next {n} tasks</button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {mode === 'type' && (
                            <div className="rq-focus-setup-row">
                                <label>Type</label>
                                <div className="rq-focus-opts">
                                    {['translate', 'review', 'approve', 'comment', 'glossary'].map((tp) => (
                                        <button type="button" key={tp} className={`rq-focus-opt ${typeVal === tp ? 'on' : ''}`} onClick={() => setTypeVal(tp)}>{TYPE_LABEL[tp]} only</button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="rq-focus-setup-row">
                            <label>Duration (optional)</label>
                            <div className="rq-focus-opts">
                                <button type="button" className={`rq-focus-opt ${durationMin == null ? 'on' : ''}`} onClick={() => setDurationMin(null)}>None</button>
                                <button type="button" className={`rq-focus-opt ${durationMin === 25 ? 'on' : ''}`} onClick={() => setDurationMin(25)}>25 min</button>
                                <button type="button" className={`rq-focus-opt ${durationMin === 45 ? 'on' : ''}`} onClick={() => setDurationMin(45)}>45 min</button>
                            </div>
                        </div>
                    </div>

                    <div className="rq-focus-meta" style={{ marginTop: 22 }}>
                        {preview.length === 0
                            ? 'Nothing matches this scope right now.'
                            : `${preview.length} task${preview.length === 1 ? '' : 's'} · ≈ ${formatMinutes(previewMin)} of work`}
                    </div>

                    <div className="rq-focus-actions">
                        <button type="button" className="rq-focus-btn" onClick={onCancel}>Cancel</button>
                        <button type="button" className="rq-focus-btn primary" disabled={preview.length === 0} onClick={() => onStart(scope, preview)}>
                            Start session
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const FocusEmpty = ({ onBackToSetup, onClose }) => (
    <div className="rq-focus-shell">
        <div className="rq-focus-top">
            <div className="rq-focus-eyebrow">Focus session</div>
            <button type="button" className="rq-focus-exit" onClick={onClose}>Close</button>
        </div>
        <div className="rq-focus-body">
            <div className="rq-focus-card" style={{ textAlign: 'center' }}>
                <div className="rq-focus-title">Nothing to focus on right now</div>
                <div className="rq-focus-meta">No open tasks match this scope. Pick a different one, or come back when work lands.</div>
                <div className="rq-focus-actions" style={{ justifyContent: 'center' }}>
                    <button type="button" className="rq-focus-btn" onClick={onClose}>Back to queue</button>
                    <button type="button" className="rq-focus-btn primary" onClick={onBackToSetup}>Change scope</button>
                </div>
            </div>
        </div>
    </div>
);

const FocusEnd = ({ session, tasksById, onAnother, onBackToQueue }) => {
    const completedTasks = (session.completedIds || []).map((id) => tasksById[id]).filter(Boolean);
    const totalMin = Math.max(0, Math.round((Date.now() - new Date(session.startedAt).getTime()) / 60000));
    const byType = {};
    completedTasks.forEach((t) => { byType[t.type] = (byType[t.type] || 0) + 1; });

    return (
        <div className="rq-focus-shell">
            <div className="rq-focus-top">
                <div className="rq-focus-eyebrow">Focus session · Wrapped</div>
                <button type="button" className="rq-focus-exit" onClick={onBackToQueue}>Close</button>
            </div>
            <div className="rq-focus-body">
                <div className="rq-focus-card" style={{ textAlign: 'center' }}>
                    <span className="rq-focus-typechip">Nice work</span>
                    <div className="rq-focus-end-num">
                        Cleared {completedTasks.length} task{completedTasks.length === 1 ? '' : 's'}
                    </div>
                    <div className="rq-focus-end-sub">{formatMinutes(totalMin)} focused</div>

                    {completedTasks.length > 0 && (
                        <div className="rq-focus-breakdown">
                            {Object.entries(byType).map(([k, v]) => (
                                <div className="rq-focus-breakdown-row" key={k}>
                                    <span>{TYPE_LABEL[k] || k}</span>
                                    <span>{v}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="rq-focus-actions" style={{ justifyContent: 'center' }}>
                        <button type="button" className="rq-focus-btn" onClick={onBackToQueue}>Back to queue</button>
                        <button type="button" className="rq-focus-btn primary" onClick={onAnother}>Start another</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Focus session — single-task-at-a-time, urgency-ordered walkthrough.
 * Persists to localStorage so Open → Editor → back round-trips resume the session.
 *
 * Props:
 *   tasks   — array of queue items (the user's open tasks). Shape from /api/dashboard/my-queue.
 *   onClose — close handler; the host should also trigger a queue refetch if state changed.
 *   routeFor — function(task) → route string, supplied by the host to keep routing logic in one place.
 */
const FocusSession = ({ tasks, onClose, routeFor }) => {
    const navigate = useNavigate();
    const [session, setSession] = useState(() => readSession());
    const [phase, setPhase] = useState(() => (readSession() ? 'running' : 'setup'));
    const [timeUpOpen, setTimeUpOpen] = useState(false);
    const [snoozePrompt, setSnoozePrompt] = useState(null);
    const [, setTick] = useState(0);
    const tickerRef = useRef(null);

    useEffect(() => { ensureFocusCss(); }, []);

    // 1s ticker while running, for timer + time-up detection
    useEffect(() => {
        if (phase !== 'running') return undefined;
        tickerRef.current = setInterval(() => setTick((n) => n + 1), 1000);
        return () => { if (tickerRef.current) clearInterval(tickerRef.current); };
    }, [phase]);

    useEffect(() => {
        if (phase !== 'running' || !session || !session.durationMin) return;
        if (session.timeUpAcknowledged) return;
        if (timeUpOpen) return;
        const dueAt = new Date(session.startedAt).getTime() + session.durationMin * 60000;
        if (Date.now() >= dueAt) setTimeUpOpen(true);
    });

    const persist = (s) => { setSession(s); writeSession(s); };
    const exit = () => { clearStoredSession(); setSession(null); onClose(); };

    // Build a stable lookup of the latest task shape (for the end screen — tasks
    // may be filtered out of `tasks` after Done, so we also snapshot the seed.)
    const tasksById = useMemo(() => {
        const map = {};
        tasks.forEach((t) => { map[t.id] = t; });
        // overlay with any cached snapshots from session creation
        if (session && session.taskSnapshots) {
            Object.entries(session.taskSnapshots).forEach(([id, snap]) => {
                if (!map[id]) map[id] = snap;
            });
        }
        return map;
    }, [tasks, session]);

    if (phase === 'setup') {
        return (
            <FocusSetup
                tasks={tasks}
                onCancel={onClose}
                onStart={(scope, preview) => {
                    writeLastScope(scope);
                    if (preview.length === 0) { setPhase('empty'); return; }
                    const snapshots = {};
                    preview.forEach((t) => { snapshots[t.id] = t; });
                    const s = {
                        scope,
                        orderedIds: preview.map((t) => t.id),
                        taskSnapshots: snapshots,
                        startedAt: new Date().toISOString(),
                        durationMin: scope.durationMin || null,
                        completedIds: [],
                        droppedIds: [], // snoozed-out-of-session
                        skipCounts: {},
                        timeUpAcknowledged: false,
                    };
                    persist(s);
                    setPhase('running');
                }}
            />
        );
    }

    if (phase === 'empty') {
        return <FocusEmpty onBackToSetup={() => setPhase('setup')} onClose={onClose} />;
    }

    if (!session) {
        // Recover gracefully if storage was cleared mid-mount
        setPhase('setup');
        return null;
    }

    const doneSet = new Set(session.completedIds || []);
    const droppedSet = new Set(session.droppedIds || []);

    // Current task = first id in orderedIds that's not done/dropped AND still present
    // in the live `tasks` array (i.e. server still considers it open).
    let curId = null;
    for (const id of session.orderedIds) {
        if (doneSet.has(id) || droppedSet.has(id)) continue;
        if (tasks.some((t) => t.id === id)) { curId = id; break; }
    }

    if (phase === 'end' || curId == null) {
        return (
            <FocusEnd
                session={session}
                tasksById={tasksById}
                onAnother={() => { clearStoredSession(); setSession(null); setPhase('setup'); }}
                onBackToQueue={exit}
            />
        );
    }

    const task = tasksById[curId];
    const doneCount = (session.completedIds || []).length;
    const totalCount = session.orderedIds.length;
    const remainingCount = session.orderedIds.filter((id) => {
        if (doneSet.has(id) || droppedSet.has(id)) return false;
        return tasks.some((t) => t.id === id);
    }).length;

    let timerLabel = '';
    let timerPct = 0;
    if (session.durationMin) {
        const elapsedMs = Date.now() - new Date(session.startedAt).getTime();
        const totalMs = session.durationMin * 60000;
        const leftMs = Math.max(0, totalMs - elapsedMs);
        timerPct = Math.min(1, elapsedMs / totalMs);
        const leftMin = Math.ceil(leftMs / 60000);
        timerLabel = leftMs <= 0 ? 'Time up' : `${formatMinutes(leftMin)} left`;
    } else {
        const elapsedMin = Math.floor((Date.now() - new Date(session.startedAt).getTime()) / 60000);
        timerLabel = `${formatMinutes(Math.max(1, elapsedMin))} elapsed`;
    }

    const doOpen = () => {
        // session is already persisted; navigating away is safe — MyQueue
        // auto-resumes when the user returns (hasActiveFocusSession()).
        const to = routeFor(task);
        if (to) navigate(to);
    };

    const doDone = () => {
        persist({ ...session, completedIds: [...(session.completedIds || []), task.id] });
    };

    const doSkip = () => {
        const skipCounts = { ...(session.skipCounts || {}) };
        skipCounts[task.id] = (skipCounts[task.id] || 0) + 1;
        if (skipCounts[task.id] >= 2) {
            setSnoozePrompt({ taskId: task.id, taskTitle: task.title });
            persist({ ...session, skipCounts });
            return;
        }
        const nextOrder = session.orderedIds.filter((id) => id !== task.id).concat(task.id);
        persist({ ...session, orderedIds: nextOrder, skipCounts });
    };

    const dropFromSession = () => {
        if (!snoozePrompt) return;
        persist({ ...session, droppedIds: [...(session.droppedIds || []), snoozePrompt.taskId] });
        setSnoozePrompt(null);
    };

    return (
        <div className="rq-focus-shell">
            <div className="rq-focus-top">
                <div className="rq-focus-eyebrow">Focus session · Task {doneCount + 1} of {totalCount}</div>
                <div className="rq-focus-timerwrap">
                    {session.durationMin && <FocusRing pct={timerPct} />}
                    <span className="rq-focus-timer-txt">{timerLabel}</span>
                    <button type="button" className="rq-focus-exit" onClick={() => setPhase('end')}>Wrap up</button>
                </div>
            </div>

            <div className="rq-focus-body">
                <div className="rq-focus-card">
                    <span className="rq-focus-typechip">{TYPE_LABEL[task.type] || task.type}</span>
                    <div className="rq-focus-title">{task.title}</div>
                    <div className="rq-focus-meta">
                        {task.project}
                        {task.pair ? ` · ${task.pair[0]} → ${task.pair[1]}` : ''}
                        {task.due ? ` · due ${task.due}` : ''}
                    </div>
                    <div className="rq-focus-size">
                        {task.segs} segment{task.segs === 1 ? '' : 's'}
                        {task.words ? ` · ${task.words.toLocaleString()} words` : ''}
                        {` · ≈ ${formatMinutes(estimateMinutes(task))}`}
                    </div>

                    <div className="rq-focus-actions">
                        <button type="button" className="rq-focus-btn" onClick={doSkip}>Skip</button>
                        <button type="button" className="rq-focus-btn" onClick={doDone}>✓ Done</button>
                        <button type="button" className="rq-focus-btn primary" onClick={doOpen}>{task.action || 'Open'}</button>
                    </div>
                </div>
            </div>

            {timeUpOpen && (
                <div className="rq-focus-modal-back">
                    <div className="rq-focus-modal">
                        <h3>Time&apos;s up</h3>
                        <p>
                            You set a {session.durationMin}-minute session. {remainingCount} task{remainingCount === 1 ? '' : 's'} left — one more, or wrap up?
                        </p>
                        <div className="rq-focus-modal-actions">
                            <button type="button" className="rq-focus-btn" onClick={() => { setTimeUpOpen(false); setPhase('end'); }}>Wrap up</button>
                            <button type="button" className="rq-focus-btn primary" onClick={() => {
                                persist({ ...session, timeUpAcknowledged: true });
                                setTimeUpOpen(false);
                            }}>Continue</button>
                        </div>
                    </div>
                </div>
            )}

            {snoozePrompt && (
                <div className="rq-focus-modal-back">
                    <div className="rq-focus-modal">
                        <h3>Skipped twice — set it aside?</h3>
                        <p>
                            &quot;{snoozePrompt.taskTitle}&quot; keeps getting skipped. Drop it from this session so it stops blocking you? It&apos;ll still be in your queue afterwards.
                        </p>
                        <div className="rq-focus-modal-actions">
                            <button type="button" className="rq-focus-btn" onClick={() => setSnoozePrompt(null)}>Keep in session</button>
                            <button type="button" className="rq-focus-btn primary" onClick={dropFromSession}>Drop from session</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FocusSession;
